import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
    ArrowLeft,
    ArrowUpRight,
    Trophy,
    Zap,
    Mail,
} from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import SponsorsMarquee from "@/components/hackathon/SponsorsMarquee";
import HackathonCalendarDate, {
    HackathonCountdown,
} from "@/components/hackathon/HackathonCalendarDate";
import HackathonFreeCapsule from "@/components/hackathon/HackathonFreeCapsule";
import HackathonVenueCapsule from "@/components/hackathon/HackathonVenueCapsule";
import HackathonFAQ from "@/components/hackathon/HackathonFAQ";
import ScheduleTimeline from "@/components/hackathon/ScheduleTimeline";
import ScrollReveal from "@/components/motion/ScrollReveal";
import HoverLift from "@/components/motion/HoverLift";
import { StaggerItem, StaggerReveal } from "@/components/motion/StaggerReveal";
import { springTransition } from "@/lib/motionVariants";
import {
    HACKATHON,
    JUDGING,
    SCHEDULE_INTRO,
    LUMA_REGISTRATION_URL,
    PRIZE_POOL,
    SPONSOR_CONTACT_EMAIL,
    SPONSOR_TIERS,
} from "@/data/agenticHackathon";

function RegisterButton({ className, children = "Register for event" }) {
    const reduceMotion = useReducedMotion();
    return (
        <motion.a
            href={LUMA_REGISTRATION_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hackathon-register-luma"
            className={className}
            whileHover={
                reduceMotion ? undefined : { y: -3, scale: 1.03 }
            }
            whileTap={reduceMotion ? undefined : { scale: 0.97, y: 0 }}
            transition={springTransition}
        >
            {children}
            <ArrowUpRight className="h-4 w-4 shrink-0" />
        </motion.a>
    );
}

export default function Hackathon() {
    return (
        <main
            data-testid="hackathon-page"
            className="min-h-screen bg-[#FAFAFA] text-zinc-950 overflow-x-hidden"
        >
            <Header />

            {/* Hero */}
            <section className="relative pt-24 sm:pt-28 md:pt-36 pb-12 sm:pb-16 md:pb-24 overflow-hidden bg-gradient-to-br from-zinc-950 via-zinc-900 to-[#1a0f0c] text-white">
                <div
                    className="pointer-events-none absolute top-10 right-0 w-[280px] sm:w-[480px] h-[280px] sm:h-[480px] rounded-full bg-[#E54D2E]/25 blur-[80px] sm:blur-[120px]"
                    aria-hidden
                />
                <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 lg:px-16 relative z-10">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm font-medium text-white/60 hover:text-white mb-6 sm:mb-10 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to home
                    </Link>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-14 items-center">
                        <div className="lg:col-span-7 order-2 lg:order-1">
                            <div className="flex flex-wrap items-center gap-2 mb-4 sm:mb-5">
                                <HackathonFreeCapsule />
                            </div>
                            <div className="flex items-stretch gap-3 sm:gap-4">
                                <HackathonCalendarDate className="shrink-0 self-stretch" />
                                <h1 className="flex min-w-0 flex-1 flex-col justify-center font-display font-black tracking-[-0.03em] leading-[0.88]">
                                    <span className="block text-[1.85rem] sm:text-5xl md:text-6xl lg:text-7xl text-white">
                                        {HACKATHON.titleTop}
                                    </span>
                                    <span className="block text-[1.85rem] sm:text-5xl md:text-6xl lg:text-7xl text-white">
                                        {HACKATHON.titleBottom}
                                    </span>
                                </h1>
                            </div>
                            <p className="mt-1 sm:mt-2 text-sm sm:text-base md:text-lg lg:text-xl font-medium text-white/55 tracking-normal">
                                {HACKATHON.titleSubtitleLead}
                                <span className="font-display font-light">
                                    {HACKATHON.titleSubtitleAccent}
                                </span>
                            </p>
                            <HackathonCountdown />
                            <p className="mt-4 sm:mt-6 text-base sm:text-lg md:text-xl text-white/75 leading-relaxed max-w-xl">
                                {HACKATHON.tagline}
                            </p>
                            <div className="mt-4 sm:mt-6 flex flex-col gap-3 items-start">
                                <HackathonVenueCapsule />
                                <span className="text-sm text-white/50">
                                    {HACKATHON.host}
                                </span>
                            </div>
                            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3">
                                <RegisterButton className="inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 sm:h-14 px-6 sm:px-8 rounded-full bg-[#E54D2E] text-white text-sm sm:text-base font-medium hover:bg-white hover:text-zinc-950 transition-colors" />
                                <a
                                    href="#sponsors"
                                    className="inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 sm:h-14 px-6 sm:px-8 rounded-full border border-white/20 text-white text-sm sm:text-base font-medium hover:bg-white/10 transition-colors"
                                >
                                    Become a sponsor
                                </a>
                            </div>
                        </div>
                        <div className="lg:col-span-5 order-1 lg:order-2">
                            <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-white/10 aspect-[16/10] sm:aspect-[4/3] max-h-[220px] sm:max-h-none">
                                <img
                                    src={HACKATHON.heroImage}
                                    alt=""
                                    className="h-full w-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 via-transparent to-transparent" />
                                <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6">
                                    <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] text-white/70">
                                        RSVP on Luma
                                    </p>
                                    <p className="mt-1 font-display text-lg sm:text-xl font-bold text-white">
                                        {HACKATHON.heroCardNote}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <SponsorsMarquee />

            {/* Prize pool */}
            <section
                id="prizes"
                data-testid="hackathon-prizes"
                className="py-14 sm:py-20 md:py-28 bg-zinc-950 text-white"
            >
                <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 lg:px-16">
                    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end justify-between gap-6 sm:gap-8 mb-10 sm:mb-12 md:mb-16">
                        <div>
                            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#E54D2E] mb-5">
                                <Trophy className="h-4 w-4" />
                                Prize pool
                            </div>
                            <h2 className="font-display text-4xl sm:text-6xl md:text-7xl font-black tracking-[-0.03em] leading-none">
                                {PRIZE_POOL.headline}
                            </h2>
                            <p className="mt-4 text-lg text-white/60">
                                {PRIZE_POOL.subline}
                            </p>
                        </div>
                        <RegisterButton className="inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 px-6 rounded-full bg-white text-zinc-950 text-sm font-medium hover:bg-[#E54D2E] hover:text-white transition-colors shrink-0" />
                    </div>
                    <StaggerReveal className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-6">
                        {PRIZE_POOL.tiers.map((tier) => (
                            <StaggerItem key={tier.place}>
                                <HoverLift className="h-full">
                                    <div
                                        className={`motion-card h-full rounded-3xl border p-6 md:p-8 flex flex-col ${
                                            tier.accent
                                                ? "border-[#E54D2E]/50 bg-[#E54D2E]/10"
                                                : "border-white/10 bg-white/[0.04]"
                                        }`}
                                    >
                                        <p className="text-xs font-mono uppercase tracking-[0.15em] text-white/50">
                                            {tier.place}
                                        </p>
                                        <p className="mt-3 font-display text-3xl md:text-4xl font-black text-[#E54D2E]">
                                            {tier.amount}
                                        </p>
                                        <p className="mt-3 text-sm text-white/65 leading-relaxed flex-1">
                                            {tier.detail}
                                        </p>
                                    </div>
                                </HoverLift>
                            </StaggerItem>
                        ))}
                    </StaggerReveal>
                </div>
            </section>

            {/* Schedule */}
            <section
                id="schedule"
                data-testid="hackathon-schedule"
                className="py-14 sm:py-20 md:py-28"
            >
                <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 lg:px-16">
                    <ScrollReveal className="max-w-2xl mb-10 sm:mb-12 md:mb-16">
                        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-5">
                            <span className="h-px w-8 bg-zinc-400" />
                            Schedule
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-[-0.03em] text-zinc-950 leading-[0.95]">
                            Pre activity &amp; hackathon day.
                        </h2>
                        <p className="mt-5 text-zinc-600 leading-relaxed">
                            {SCHEDULE_INTRO}
                        </p>
                    </ScrollReveal>
                    <ScrollReveal delay={0.1}>
                        <ScheduleTimeline />
                    </ScrollReveal>
                </div>
            </section>

            {/* About + judging */}
            <section className="py-14 sm:py-20 md:py-28 bg-zinc-950 text-white border-y border-white/[0.06]">
                <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-16">
                    <ScrollReveal variant="slideInLeft">
                        <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight text-white">
                            What you&apos;ll build
                        </h2>
                        <p className="mt-5 text-white/70 leading-relaxed">
                            {HACKATHON.buildBrief}
                        </p>
                        <p className="mt-4 text-white/70 leading-relaxed">
                            Build before event day, then come ready to present
                            and compete for the bounty.
                        </p>
                        <RegisterButton className="mt-6 inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 sm:h-14 px-8 rounded-full bg-[#E54D2E] text-white text-sm sm:text-base font-semibold shadow-lg shadow-[#E54D2E]/25 hover:bg-white hover:text-zinc-950 hover:shadow-white/10 transition-all">
                            Register on Luma to secure your spot
                        </RegisterButton>
                    </ScrollReveal>
                    <ScrollReveal variant="slideInRight" delay={0.1}>
                        <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight text-white">
                            Judging criteria
                        </h2>
                        <ul className="mt-5 space-y-3">
                            {JUDGING.map((item) => (
                                <li
                                    key={item}
                                    className="flex gap-3 text-white/70 leading-relaxed"
                                >
                                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#E54D2E]" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </ScrollReveal>
                </div>
            </section>

            {/* Call for sponsors */}
            <section
                id="sponsors"
                data-testid="call-for-sponsors"
                className="py-14 sm:py-20 md:py-28"
            >
                <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 lg:px-16">
                    <div className="max-w-2xl mb-10 sm:mb-12 md:mb-16">
                        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-5">
                            <span className="h-px w-8 bg-zinc-400" />
                            Call for sponsors
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-[-0.03em] text-zinc-950 leading-[0.95]">
                            Put your brand in front of{" "}
                            <span className="text-[#E54D2E]">builders</span>.
                        </h2>
                        <p className="mt-5 text-zinc-600 leading-relaxed">
                            We&apos;re lining up partners for the{" "}
                            {HACKATHON.title} in {HACKATHON.dateLabel}. Reach
                            developers, founders, and students — with
                            visibility on stage, web, and social.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                        {SPONSOR_TIERS.map((tier) => (
                            <div
                                key={tier.name}
                                className="rounded-3xl border border-black/[0.06] bg-white p-6 md:p-8 shadow-lg shadow-zinc-950/5"
                            >
                                <h3 className="font-display text-xl font-bold text-zinc-950">
                                    {tier.name}
                                </h3>
                                <ul className="mt-5 space-y-2.5">
                                    {tier.perks.map((perk) => (
                                        <li
                                            key={perk}
                                            className="text-sm text-zinc-600 flex gap-2"
                                        >
                                            <span className="text-[#E54D2E]">
                                                ·
                                            </span>
                                            {perk}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <div className="rounded-2xl sm:rounded-3xl bg-[#E54D2E] text-white p-6 sm:p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6 sm:gap-8">
                        <div>
                            <h3 className="font-display text-2xl md:text-3xl font-black tracking-tight">
                                Partner with Build Bout
                            </h3>
                            <p className="mt-3 text-white/85 max-w-lg leading-relaxed">
                                Tell us what you&apos;d like to contribute —
                                cash, credits, venue, food, or prizes. We&apos;ll
                                send a simple sponsorship deck.
                            </p>
                        </div>
                        <a
                            href={`mailto:${SPONSOR_CONTACT_EMAIL}?subject=Sponsor%20%E2%80%94%20Agentic%20Hack%20Payments%20%26%20Robots`}
                            className="inline-flex w-full md:w-auto items-center justify-center gap-2 h-12 sm:h-14 px-8 rounded-full bg-zinc-950 text-white font-medium hover:bg-white hover:text-zinc-950 transition-colors shrink-0"
                        >
                            <Mail className="h-4 w-4" />
                            Email to sponsor
                        </a>
                    </div>
                </div>
            </section>

            <SponsorsMarquee />

            <HackathonFAQ />

            {/* Final CTA */}
            <section className="py-14 sm:py-20 md:py-28">
                <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 lg:px-16">
                    <div className="rounded-2xl sm:rounded-3xl bg-zinc-950 text-white p-8 sm:p-10 md:p-16 text-center">
                        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tight">
                            Ready for Agentic Hack?
                        </h2>
                        <p className="mt-4 text-white/70 max-w-lg mx-auto leading-relaxed">
                            Register on Luma — verification and wallet steps
                            happen there. See you in {HACKATHON.dateLabel}.
                        </p>
                        <RegisterButton className="mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 sm:h-14 px-8 rounded-full bg-[#E54D2E] text-white text-base font-medium hover:bg-white hover:text-zinc-950 transition-colors mx-auto max-w-sm sm:max-w-none" />
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}
